'use client';

import { ReactNode, useEffect, useState } from 'react';
import { cn } from '@/shadcn/lib/utils';

/**
 * Homepage "search mode" (homes.com situacia).
 *
 * Kym user nezacne hladat, homepage je klasicka (TOP ponuky, makleri, RK...).
 * Ked zacne pisat do smart inputu, sekcie pod hero sa vymenia za split view:
 * vlavo grid ponuk (2 in a row), vpravo sticky mapa.
 *
 * Komunikacia cez custom DOM event, aby filter (client) a homepage (server)
 * neboli import-coupled - rovnaky pattern ako `rx-vox-chat-open`.
 */
export const HOME_SEARCH_EVENT = 'rx-home-search';

export type HomeSearchDetail = { active: boolean; source?: 'input' | 'reset'; query?: string };

export function emitHomeSearch(
    active: boolean,
    source: HomeSearchDetail['source'] = 'input',
    query = '',
) {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
        new CustomEvent<HomeSearchDetail>(HOME_SEARCH_EVENT, { detail: { active, source, query } }),
    );
}

/** klik na logo / "spat na uvod": vypne search mode a vycisti smart input */
export function emitHomeReset() {
    emitHomeSearch(false, 'reset');
}

type Props = {
    /** split view s vysledkami - mountne sa lazy pri prvej aktivacii (mapbox) */
    results: ReactNode;
    /** klasicky obsah homepage pod hero */
    children: ReactNode;
};

export function HomeBody(props: Props) {
    const [active, setActive] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        const onSearch = (e: Event) => {
            const next = Boolean((e as CustomEvent<HomeSearchDetail>).detail?.active);
            setActive(next);
            if (next) setMounted(true);
        };
        window.addEventListener(HOME_SEARCH_EVENT, onSearch);
        return () => window.removeEventListener(HOME_SEARCH_EVENT, onSearch);
    }, []);

    return (
        <div className="relative">
            <style
                dangerouslySetInnerHTML={{
                    __html: `
@keyframes rx-home-in { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
.rx-home-in { animation: rx-home-in .55s cubic-bezier(.22,.61,.36,1) both; }
@media (prefers-reduced-motion: reduce) { .rx-home-in { animation: none; } }
`,
                }}
            />
            {/* key prepina animaciu pri kazdom prepnuti; mapa ostava mountnuta (hidden) */}
            <div key={active ? 'd-off' : 'd-on'} className={cn('rx-home-in', active && 'hidden')}>
                {props.children}
            </div>
            {mounted && (
                <div className={cn(active && 'rx-home-in', !active && 'hidden')}>
                    {props.results}
                </div>
            )}
        </div>
    );
}
